/**
 * Theme-mode context and hook.
 *
 * Split out of ThemeProvider.tsx so that file only exports components, which is
 * what React Fast Refresh needs to hot-reload it cleanly.
 */
import { createContext, useContext } from 'react';

import { type ThemeMode } from './tokens';

/** What the user picked. `system` defers to `prefers-color-scheme`. */
export type ThemePreference = 'light' | 'dark' | 'system';

export interface ThemeModeContextValue {
  /** What the user picked. */
  preference: ThemePreference;
  /** What is actually rendering right now, after resolving `system`. */
  mode: ThemeMode;
  setPreference: (next: ThemePreference) => void;
  /** Convenience for the top-bar toggle: light <-> dark, leaving `system`. */
  toggle: () => void;
}

export const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used inside <ThemeProvider>');
  return ctx;
}
